import { Instagram, Facebook, MessageCircle, Music2, Twitter, Youtube, Globe, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { isPremium } from "@/data/plans";

const CHANNELS = [
  { key: "website_url", Icon: Globe, label: "Website", labelEs: "Sitio web", social: false },
  { key: "whatsapp_url", Icon: MessageCircle, label: "WhatsApp", labelEs: "WhatsApp", social: true },
  { key: "instagram_url", Icon: Instagram, label: "Instagram", labelEs: "Instagram", social: true },
  { key: "facebook_url", Icon: Facebook, label: "Facebook", labelEs: "Facebook", social: true },
  { key: "tiktok_url", Icon: Music2, label: "TikTok", labelEs: "TikTok", social: true },
  { key: "twitter_url", Icon: Twitter, label: "X", labelEs: "X", social: true },
  { key: "youtube_url", Icon: Youtube, label: "YouTube", labelEs: "YouTube", social: true },
];

const isLoop = (listing) => /loop experience/i.test(listing?.name || "");

/**
 * The profile's one Connect card: a charcoal strip holding every channel the
 * business can be reached on as a circular button, with the spaces still to be
 * filled marked by a small dot. A profile whose links are locked by plan closes
 * with the line that opens them.
 */
export default function SocialBar({ listing, lang = "en" }) {
  const loopShowcase = isLoop(listing);
  const premium = loopShowcase || isPremium(listing);
  const es = lang === "es";
  const channels = CHANNELS;

  const circle =
    "group relative inline-grid h-10 w-10 shrink-0 place-items-center rounded-full border transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1a1a] " +
    (premium
      ? "border-white/[0.12] bg-[#292929] text-[#a8a8a8] hover:border-white/25 hover:bg-[#343434] hover:text-white"
      : "cursor-default border-white/[0.08] bg-[#232323] text-[#7d7d7d]");

  return (
    <section
      aria-label={es ? "Enlaces de contacto y redes sociales" : "Contact and social links"}
      className="rounded-[16px] bg-[#1a1a1a] px-4 py-3.5 ring-1 ring-white/[0.06]"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[14px] font-semibold tracking-[-0.01em] text-white">
          {es ? "Conecta" : "Connect"}
        </h2>
        {!premium && (
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8a8a8a]">Premium</span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-1.5">
        {channels.map(({ key, Icon, label, labelEs, social: isSocial }) => {
          const url = listing?.[key];
          const displayLabel = es ? labelEs : label;
          const unlocked = premium && !!url;
          const websiteBasic = !isSocial && !!url;
          const active = unlocked || websiteBasic;
          const labelNote = !premium && isSocial
            ? (es ? "Disponible con Premium" : "Available with Premium")
            : !url && key === "website_url"
              ? (es ? "Añadir sitio web" : "Add website")
              : premium && !url
                ? (es ? "Añadir detalles" : "Add details")
                : displayLabel;
          const content = (
            <>
              <Icon className="h-[17px] w-[17px]" strokeWidth={1.7} aria-hidden="true" />
              {!active && (
                <span
                  className="absolute right-0 top-0 h-[7px] w-[7px] rounded-full bg-[#8a8a8a] ring-2 ring-[#232323]"
                  aria-hidden="true"
                />
              )}
            </>
          );

          if (active) {
            const normalizedUrl = key === "website_url" && url && !/^https?:\/\//i.test(url) ? "https://" + url : url;
            return <a key={key} href={normalizedUrl} target="_blank" rel="noopener noreferrer" aria-label={displayLabel} title={displayLabel} className={circle}>{content}</a>;
          }
          if (!premium) {
            return <Link key={key} to="/business" aria-label={labelNote} title={labelNote} className={circle + " hover:border-white/20 hover:text-[#bdbdbd]"}>{content}</Link>;
          }
          return <div key={key} aria-label={labelNote} title={labelNote} className={circle + " opacity-70"}>{content}</div>;
        })}
      </div>

      {!premium && (
        <>
          <div className="mt-3.5 border-t border-white/[0.09]" />
          <Link
            to="/business"
            className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#d0d0d0] transition-colors hover:text-white"
          >
            {es ? "Mejorar a Premium" : "Upgrade to Premium"}
            <Lock className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
          </Link>
        </>
      )}
    </section>
  );
}